<script lang="ts" setup>
import type { FieldSettings } from "@/api/visitorFields";
import { useValidation } from "@/composables/validation";
import {
  ElFormItem,
  ElCheckboxGroup,
  ElCheckbox,
  type CheckboxGroupValueType,
} from "element-plus";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

export interface Props {
  options?: string[];
  modelValue?: string[];
  disabled: boolean;
  required?: boolean;
  id?: string;
  isPreregistration?: boolean;
  settings: FieldSettings;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "update:model-value", value: string[]): void;
}>();

const input = ref<CheckboxGroupValueType>(props.modelValue ?? []);
const formItemRef = ref<InstanceType<typeof ElFormItem> | null>(null);

const { validateRequiredField, validateApprovalField, validateFormItem } =
  useValidation();
const { locale } = useI18n();

const rules = computed(() => {
  const listRule = [];
  if (props.required) {
    listRule.push(validateRequiredField(input));
  }

  if (props.isPreregistration) {
    listRule.push(validateApprovalField(input, props.settings));
  }

  return listRule;
});

watch(
  () => props.modelValue,
  (value) => (input.value = value ?? [])
);

watch(locale, () => {
  const fieldRefs = { formItemRef: formItemRef.value };
  validateFormItem(fieldRefs);
});
</script>

<template>
  <el-form-item
    ref="formItemRef"
    :prop="id"
    :rules="rules"
    :required="required"
  >
    <template #label>
      <slot name="label" />
    </template>
    <el-checkbox-group
      v-model="input"
      :disabled="disabled"
      class="radio-group"
      @change="emit('update:model-value', $event as string[])"
    >
      <el-checkbox
        v-for="option in options"
        :key="option"
        :label="option"
      >
        {{ option }}
      </el-checkbox>
    </el-checkbox-group>
  </el-form-item>
</template>

<style lang="css" scoped>
.radio-group {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 4px;
}

.radio-group :deep(.so-checkbox) {
  align-items: flex-start;
  padding-bottom: 8px;
  margin-right: 30px;
}

.radio-group :deep(.so-checkbox__input) {
  margin-top: 3px;
}

.radio-group :deep(.so-checkbox__label) {
  line-height: 1.375rem;
  word-break: break-word;
  white-space: pre-wrap;
}
</style>
